import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import { Button, Input } from '../components/ui';
import { useAuth } from '../features/auth/AuthProvider';
import styles from './LoginPage.module.scss';

const MAX_LOGIN_ATTEMPTS = 5;

export function LoginPage() {
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);

  const isLockedOut = attemptCount >= MAX_LOGIN_ATTEMPTS;

  const mutation = useMutation({
    mutationFn: async () => {
      await auth.login({ email, password });
    },
    onSuccess: () => {
      const destination = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(destination || '/', { replace: true });
    },
    onError: () => {
      setAttemptCount((n) => n + 1);
    },
  });

  const errorMessage = isLockedOut
    ? t('auth.tooManyAttempts')
    : mutation.error instanceof ApiError && mutation.error.status === 401
      ? t('auth.invalidCredentials')
      : mutation.error instanceof ApiError && mutation.error.status === 403
        ? t('auth.adminOnly')
      : t('auth.signInError');

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.brandLabel}>White&amp;Flex</span>
          <h1 className={styles.title}>{t('auth.signInTitle')}</h1>
          <p className={styles.subtitle}>{t('auth.signInHint')}</p>
        </div>

        {mutation.isError || isLockedOut ? (
          <div className={styles.alert} role="alert">
            {errorMessage}
          </div>
        ) : null}

        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (!isLockedOut && !mutation.isPending) {
              mutation.mutate();
            }
          }}
        >
          <div className={styles.fields}>
            <Input
              id="email"
              type="email"
              label={t('auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <Input
              id="password"
              type="password"
              label={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={mutation.isPending || isLockedOut}
          >
            {mutation.isPending ? t('auth.signingIn') : t('auth.signIn')}
          </Button>

          {/* Stays active during lockout — that is when it is needed most. */}
          <Link to="/forgot-password" state={{ email }} className={styles.textLink}>
            {t('auth.forgotPasswordLink')}
          </Link>

          <p className={styles.accessNote}>{t('auth.accessRestricted')}</p>
        </form>
      </div>
    </div>
  );
}
